import React, { useState, useEffect } from 'react';
import { connectWithPrivateKey } from '../EthersSetup.js';

/**
 * PrivateKeyWallet Component
 * 
 * Secure private key input component with:
 * - Password-style input with show/hide toggle
 * - Security warnings and disclaimers
 * - New wallet generation option
 * - Clear backup instructions
 * - Input validation
 */
const PrivateKeyWallet = ({ onSuccess, onError, onCancel }) => {
  // Auto-detect and prefill RPC URL based on current environment
  const getDefaultRpcUrl = () => {
    const hostname = window.location.hostname;

    if (hostname === 'devcli.lazymagpie.de') {
      return 'https://devcli.lazymagpie.de:8546/';
    } else if (hostname === 'dev.lazymagpie.de') {
      return 'https://dev.lazymagpie.de:8546/';
    } else if (hostname === 'test.lazymagpie.de') {
      // Polygon Amoy testnet - using default public RPC
      return 'https://rpc-amoy.polygon.technology/';
    } else if (hostname === 'preprod.lazymagpie.de') {
      // Polygon mainnet preprod - using dedicated GameMaker RPC server
      return 'https://prod.lazymagpie.de:8547/';
    } else if (hostname === 'prod.lazymagpie.de') {
      // Polygon mainnet production - using dedicated GameMaker RPC server
      return 'https://prod.lazymagpie.de:8547/';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8545';
    }

    // Default to localhost for unknown environments
    return 'http://localhost:8545';
  };

  const defaultRpcUrl = getDefaultRpcUrl();
  const [privateKey, setPrivateKey] = useState('');
  const [rpcUrl, setRpcUrl] = useState(defaultRpcUrl);
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  const [isConnecting, setIsConnecting] = useState(false);
  const [generatedWallet, setGeneratedWallet] = useState(null);
  const [showGenerateOptions, setShowGenerateOptions] = useState(false);
  const [persistenceMode] = useState('indefinite'); // Fixed to permanent storage

  // Clear sensitive data on unmount for security (but preserve RPC URL)
  useEffect(() => {
    return () => {
      setPrivateKey('');
      // Don't clear RPC URL as it's not sensitive and should persist
      setGeneratedWallet(null);
    };
  }, []);

  const handleConnect = async () => {
    if (!privateKey.trim()) {
      onError('Please enter a private key');
      return;
    }

    if (!rpcUrl.trim()) {
      onError('Please enter an RPC URL');
      return;
    }

    setIsConnecting(true);
    try {
      await connectWithPrivateKey(privateKey.trim(), rpcUrl.trim(), persistenceMode);
      // Clear sensitive data from state immediately after successful connection
      setPrivateKey('');
      // Don't clear RPC URL - keep it for user convenience
      onSuccess();
    } catch (error) {
      onError(error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const generateNewWallet = () => {
    // Generate a new wallet using ethers (we'll import this from our provider)
    const ethers = require('ethers');
    const wallet = ethers.Wallet.createRandom();
    
    setGeneratedWallet({
      privateKey: wallet.privateKey,
      address: wallet.address,
      mnemonic: wallet.mnemonic?.phrase || null
    });
    setShowGenerateOptions(false);
  };

  const useGeneratedWallet = () => {
    if (generatedWallet) {
      setPrivateKey(generatedWallet.privateKey);
      // Keep wallet details visible after "Use This Wallet" is clicked
      // Don't clear generatedWallet so details remain open
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const isValidPrivateKey = (key) => {
    if (!key) return false;
    const cleanKey = key.startsWith('0x') ? key.slice(2) : key;
    return cleanKey.length === 64 && /^[0-9a-fA-F]+$/.test(cleanKey);
  };

  const isValidRpcUrl = (url) => {
    if (!url) return false;
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Custom RPC Wallet</h2>
        <p className="text-sm text-gray-600">
          Connect using your private key and your own RPC endpoint for direct blockchain access.
        </p>
      </div>

          {/* Generate New Wallet Option */}
          <div className="mb-6">
            {!generatedWallet && (
              <button
                onClick={generateNewWallet}
                className="w-full px-4 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
              >
                Generate New Wallet
              </button>
            )}
          </div>

          {/* Generated Wallet Display */}
          {generatedWallet && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              {/* Security Warning */}
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-red-800">
                      ⚠️ IMPORTANT: Backup Required!
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      We strongly recommend you write down or copy to a secure location the private key or mnemonic phrase before proceeding.
                    </p>
                  </div>
                </div>
              </div>
              
              <h4 className="font-semibold text-green-800 mb-2">New Wallet Generated</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <label className="block font-medium text-gray-700">Address:</label>
                  <div className="flex items-center">
                    <code className="text-xs bg-gray-100 p-1 rounded flex-1">{generatedWallet.address}</code>
                    <button
                      onClick={() => copyToClipboard(generatedWallet.address)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      title="Copy address"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block font-medium text-gray-700">Private Key:</label>
                  <div className="flex items-center">
                    <code className="text-xs bg-gray-100 p-1 rounded flex-1 break-all">{generatedWallet.privateKey}</code>
                    <button
                      onClick={() => copyToClipboard(generatedWallet.privateKey)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      title="Copy private key"
                    >
                      📋
                    </button>
                  </div>
                </div>
                {generatedWallet.mnemonic && (
                  <div>
                    <label className="block font-medium text-gray-700">Mnemonic:</label>
                    <div className="flex items-center">
                      <code className="text-xs bg-gray-100 p-1 rounded flex-1">{generatedWallet.mnemonic}</code>
                      <button
                        onClick={() => copyToClipboard(generatedWallet.mnemonic)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        title="Copy mnemonic"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 space-x-2">
                <button
                  onClick={useGeneratedWallet}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Use This Wallet
                </button>
                <button
                  onClick={generateNewWallet}
                  className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                >
                  Generate Another
                </button>
              </div>
            </div>
          )}

          {/* Private Key Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Private Key
            </label>
            <div className="relative">
              <input
                type={showPrivateKey ? 'text' : 'password'}
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                placeholder="Enter your private key (0x...)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPrivateKey(!showPrivateKey)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPrivateKey ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12l3.878-3.878M9.878 9.878L6.343 6.343m7.535 7.535L17.657 17.657" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {privateKey && !isValidPrivateKey(privateKey) && (
              <p className="mt-1 text-sm text-red-600">
                Invalid private key format. Must be 64 hex characters (with or without 0x prefix).
              </p>
            )}
          </div>

          {/* RPC URL Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              RPC Endpoint URL
            </label>
            <input
              type="url"
              value={rpcUrl}
              onChange={(e) => setRpcUrl(e.target.value)}
              placeholder={`${defaultRpcUrl} (auto-detected)`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="mt-1 text-xs text-gray-500">
              <p>✅ Auto-detected URL for <strong>{window.location.hostname}</strong> environment</p>
              {(window.location.hostname === 'prod.lazymagpie.de' || window.location.hostname === 'preprod.lazymagpie.de') ? (
                <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-green-700 font-medium">🎮 Using dedicated GameMaker RPC server</p>
                  <p className="text-green-600">Optimized for GameMaker contracts with caching and faster responses</p>
                  <p className="text-gray-500">Alternative: <code>https://polygon-rpc.com/</code> (public Polygon RPC)</p>
                </div>
              ) : (
                <div className="mt-1">
                  <p>You can edit this URL to use a different network or RPC provider</p>
                  {window.location.hostname === 'test.lazymagpie.de' && (
                    <p className="text-gray-500">Current: Public Polygon Amoy testnet RPC</p>
                  )}
                </div>
              )}
            </div>
            {rpcUrl && !isValidRpcUrl(rpcUrl) && (
              <p className="mt-1 text-sm text-red-600">
                Invalid RPC URL format. Must be a valid HTTP/HTTPS URL.
              </p>
            )}
          </div>

          {/* Storage Information */}
          <div className="mb-6">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <svg className="h-4 w-4 text-blue-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-xs text-blue-800 font-medium">Private Key Storage</p>
                  <ul className="text-xs text-blue-700 mt-1 space-y-1">
                    <li>• Stored permanently in browser until you disconnect</li>
                    <li>• Never sent to servers - stored locally only</li>
                    <li>• Use only on trusted devices you control</li>
                    <li>• Click "Disconnect" to completely remove saved key</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleConnect}
              disabled={!isValidPrivateKey(privateKey) || !isValidRpcUrl(rpcUrl) || isConnecting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Connect'}
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {/* Additional Security Info */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-800 mb-2">Why Private Key Mode?</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• More reliable event detection for real-time games</li>
              <li>• Direct connection to blockchain RPC</li>
              <li>• No dependency on browser extension connectivity</li>
              <li>• Faster transaction signing</li>
            </ul>
          </div>
    </div>
  );
};

export default PrivateKeyWallet;